import { Box, Chip, styled, Tooltip, tooltipClasses, useTheme } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { BaseURL, BucketURL } from "../ApiSetup/Api";

/**
 * Constructs a proper image URL from a file path
 * Handles: full URLs, leading slashes, and proper base URL formatting
 * @param {string} file - The file path or URL
 * @returns {string} The complete image URL
 */
export const getImageUrl = (file) => {
    if (!file) return '';
    // If already a full URL, return as-is
    if (typeof file === 'string' && (file.startsWith('http://') || file.startsWith('https://'))) {
        return file;
    }
    // Handle array input - take first element
    const filePath = Array.isArray(file) ? file[0] : file;
    if (!filePath) return '';
    // Remove leading slash from file path if present
    const cleanFile = typeof filePath === 'string' && filePath.startsWith('/') ? filePath.slice(1) : filePath;
    // Ensure BucketURL ends with slash
    const baseUrl = BucketURL.endsWith('/') ? BucketURL : `${BucketURL}/`;
    const fullUrl = `${baseUrl}${cleanFile}`;
    console.log('[getImageUrl] Input:', file, '-> Output:', fullUrl);
    return fullUrl;
};

/**
 * Builds an authenticated admin proxy URL for bucket files (PDF preview, downloads).
 * Returns null when the file is not from the configured bucket.
 */
export const getFileProxyUrl = (filePathOrUrl) => {
    if (!filePathOrUrl) return null;

    let path = filePathOrUrl;
    if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) {
        const bucketBase = BucketURL.endsWith('/') ? BucketURL : `${BucketURL}/`;
        if (!path.startsWith(bucketBase)) return null;
        path = path.slice(bucketBase.length);
    } else if (Array.isArray(path)) {
        path = path[0];
    }

    if (!path) return null;
    const cleanPath = typeof path === 'string' && path.startsWith('/') ? path.slice(1) : path;
    const base = BaseURL.endsWith('/') ? BaseURL : `${BaseURL}/`;
    return `${base}admin/proxy-file?path=${encodeURIComponent(cleanPath)}`;
};

export const downloadFileFromBlob = (data, filename, mime, bom) => {
    const blobData = typeof bom !== "undefined" ? [bom, data] : [data];
    const blob = new Blob(blobData, { type: mime || "application/octet-stream" });
    if (typeof window.navigator.msSaveBlob !== "undefined") {
        // IE workaround for "HTML7007: One or more blob URLs were
        // revoked by closing the blob for which they were created.
        // These URLs will no longer resolve as the data backing
        // the URL has been freed."
        window.navigator.msSaveBlob(blob, filename);
    } else {
        const blobURL =
            window.URL && window.URL.createObjectURL
                ? window.URL.createObjectURL(blob)
                : window.webkitURL.createObjectURL(blob);
        const tempLink = document.createElement("a");
        tempLink.style.display = "none";
        tempLink.href = blobURL;
        tempLink.setAttribute("download", filename);

        // Safari workaround
        if (typeof tempLink.download === "undefined") {
            tempLink.setAttribute("target", "_blank");
        }

        document.body.appendChild(tempLink);
        tempLink.click();

        // Cleanup after download
        setTimeout(function () {
            document.body.removeChild(tempLink);
            window.URL.revokeObjectURL(blobURL);
        }, 200);
    }
};

export default function ErrorHandler(result, showToast = true) {
    if (result?.meta?.requestStatus === "rejected") {
        const err = result?.payload || result?.error;
        if (showToast) {
            toast.error(err?.message || err?.data?.message || "Request failed");
        }
        return false;
    }

    let data = result?.payload;
    
    // Handle network errors or undefined responses silently
    if (!data) {
        if (showToast) {
            console.error("API Error: No response data", result);
        }
        return false;
    }
    
    if (data?.status) {
        if (data?.status === 500 || data?.status === 502) {
            if (showToast) toast.error(data?.message || "Server error");
            return false;
        } else if (data?.status === 404) {
            if (showToast) toast.error(data?.message || "Not found");
            return false;
        } else if (data?.status === 400) {
            if (showToast) toast.error(data?.message || "Bad request");
            return false;
        } else if (data?.status === 409) {
            if (showToast) toast.error(data?.message || "Conflict");
            return false;
        } else if (data?.status === 403) {
            if (showToast) toast.error(data?.message || "Forbidden");
            return false;
        } else if (data?.status === 401) {
            localStorage.clear();
            return false;
        } else {
            return true;
        }
    } else {
        // Check if it's a successful response without status field
        if (data?.data || data?.response || data?.code === 1) {
            return true;
        }
        if (showToast) {
            console.error("API Error: Unexpected response", result);
        }
        return false;
    }
}

export const getExtensionFromPath = (path) => {
    if (!path || typeof path !== 'string') return '';
    const clean = path.split('?')[0].split('#')[0];
    const filename = clean.split('/').pop() || '';
    const dotIndex = filename.lastIndexOf('.');
    if (dotIndex <= 0) return '';
    return filename.slice(dotIndex + 1).toLowerCase();
};

const EXTENSION_TO_FILE_ICON = {
    png: 'png',
    jpg: 'jpg',
    jpeg: 'jpg',
    gif: 'gallery',
    webp: 'jpg',
    bmp: 'jpg',
    svg: 'jpg',
    heic: 'jpg',
    heif: 'jpg',
    doc: 'doc',
    docx: 'doc',
    ppt: 'ppt',
    pptx: 'ppt',
    pdf: 'pdf',
    xls: 'xls',
    xlsx: 'xls',
    txt: 'txt',
    mp4: 'video-player',
    webm: 'video-player',
    mov: 'video-player',
    mp3: 'music',
    wav: 'music',
};

export const getFileIcon = (input) => {
    const sources = [];
    if (typeof input === 'string') {
        sources.push(input);
    } else if (Array.isArray(input)) {
        sources.push(...input);
    } else {
        return 'file';
    }

    for (const url of sources) {
        if (!url || typeof url !== 'string') continue;
        const extension = getExtensionFromPath(url);
        if (extension && EXTENSION_TO_FILE_ICON[extension]) {
            return EXTENSION_TO_FILE_ICON[extension];
        }
    }

    return 'file';
};


export function SidebarIcon({ src, width, height }) {
    return (
        <>
            <img src={src} alt='icon' width={width} height={height} />
        </>
    )
}
export const converOctateToType = async ({ url, link, type }) => {
    const fetchUrl = url || link;
    if (!fetchUrl) {
        throw new Error("No URL provided for file conversion");
    }

    const proxyUrl = getFileProxyUrl(fetchUrl);
    const headers = {};
    if (proxyUrl) {
        const token = localStorage.getItem("auth_token");
        if (token) headers.Authorization = token;
    }

    const b = await fetch(proxyUrl || fetchUrl, { headers }).then((d) => {
        if (!d.ok) {
            throw new Error(`Failed to fetch file (${d.status})`);
        }
        return d.blob();
    });
    return type ? b.slice(0, b.size, type) : b;
};
export function ImageWrapper({ src, width, height }) {
    return <img src={src} alt='icon' width={width} height={height} />
}

export function CommonChip({ label, height, width, margin, padding, fontSize }) {
    const safeLabel = label || "-";
    return (
        <>
            <Chip label={safeLabel}
                sx={{
                    backgroundColor: safeLabel === "Pending" ?
                        "#F8D270" : safeLabel === "Rejected" ?
                            "#eb5454" : safeLabel === "Partial-Rejected" ?
                                "#eb5454" : safeLabel === "Not-Interested" ?
                                    "#eb5454" : (safeLabel === "Approved" || safeLabel === "Accepted") ?
                                        "#00D47EB8" : (safeLabel === "Draft" || safeLabel === "Login Users") ?
                                            "#c8c8c8" : safeLabel === "Re-Uploaded" ?
                                                "#75bac4" : "#c8c8c8",
                    color: "#FFFFFF",
                    height: height || "28px",
                    width: width,
                    margin: margin,
                    fontSize: fontSize,
                    "& .MuiChip-label": {
                        padding: padding
                    },
                    borderRadius:'6px'
                }}
            />
        </>
    )
}

export function PaymentStatus({ label, height, width, margin, padding }) {
    const theme = useTheme()
    const safeLabel = label || "-";
    return (
        <>
            <Chip label={safeLabel}
                sx={{
                    backgroundColor: safeLabel === "SUCCESS" ? theme.palette.success.main
                        : (safeLabel === "FAILURE" || safeLabel?.toLowerCase() === "cancel") ? theme.palette.error.main
                            : safeLabel === "PENDING" ? theme.palette.primary.main : "#0C0C0C",
                    color: "#FFFFFF",
                    height: height || "28px",
                    width: width,
                    margin: margin,
                    "& .MuiChip-label": {
                        padding: padding
                    }
                }}
            />
        </>
    )
}

export function CommonChipFollowUpStatus({ label, height, fontSize }) {
    const safeLabel = label || "-";
    return (
        <>
            <Chip label={safeLabel}
                sx={{
                    backgroundColor: safeLabel === "Pending" ?
                        "#F8D270" : safeLabel === "OnHold" ?
                            "#eb5454" : safeLabel === "Follow up running on call" ?
                                "#eb5454" : safeLabel === "Meeting done but confirmation pending" ?
                                    "#eb5454" : (safeLabel === "Not-Intersted" || safeLabel === "Not-Interested") ?
                                        "#eb5454" : safeLabel === "Confirmed" ?
                                            "#00D47EB8" : safeLabel === "Coming for verification" ?
                                                "#00D47EB8" : safeLabel === "Meeting done" ?
                                                    "#c8c8c8" : safeLabel === "Meeting time scheduled but not come yet" ?
                                                        "#75bac4" : "#FFFFFF",
                    color: "#FFFFFF",
                    fontSize: fontSize || "12px", height: height || "20px",
                    borderRadius:'6px'
                }}
            />
        </>
    )
}

export const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

export const ReadMore = ({ content, align, contentLength = 50 }) => {
    const theme = useTheme()
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpanded = () => setIsExpanded((prev) => !prev);

    const truncatedContent =
        content?.length > contentLength
            ? content.substring(0, contentLength)
            : content;

    return (
        <Box align={align || "center"}>
            {isExpanded ? content : truncatedContent}
            {content?.length > contentLength && (
                <span
                    onClick={toggleExpanded}
                    style={{ color: theme?.palette?.primary?.main, cursor: "pointer", marginLeft: 8, fontSize: 13, fontWeight: 'bold' }}
                >
                    {isExpanded ? "ReadLess" : "ReadMore"}
                </span>
            )}
        </Box>
    );
};

/** PPSK staff, or a Karobari / KOROBARI MEMBERS reviewer, can audit pending documents. */
export const canAuditPendingDocuments = (user) => {
    if (!user?.organisationName || user.organisationName === "PPSK") return true;
    const roles = [user.loginRole, ...(Array.isArray(user.extraRole) ? user.extraRole : [])];
    return roles.some((role) => /k[ao]robari/i.test(String(role || "")));
};

export const checkAccess = (pathname, pageAccessConfig, ...actionsType) => {
    // SuperAdmin has all permissions by default
    const userData = sessionStorage.getItem('userData') || localStorage.getItem('userData');
    if (userData) {
        try {
            const user = JSON.parse(userData);
            if (user?.loginRole === "SuperAdmin") {
                return true;
            }
        } catch (e) {
            // Continue with normal check
        }
    }
    
    // Handle /admin prefix - extract the actual page path
    let basePathname = pathname;
    if (pathname.startsWith('/admin/')) {
        basePathname = pathname.replace('/admin', '');
    } else if (pathname === '/admin') {
        basePathname = '/';
    } else {
        basePathname = `/${pathname.split('/')[1]}`;
    }
    const access = pageAccessConfig?.find((val) => val.path === basePathname);
    return actionsType.every((type) => access?.actionsType?.includes(type));
};


export const formattedDate = (date) => { return date?.split('/')?.reverse()?.join('/'); }

export const calculateAgeAndMonth = (dob) => {
    // Parse the input date string to detect format and create a Date object
    const parts = dob?.includes('/') ? dob?.split('/') : dob?.split('-');
    let birthDate;

    if (parts?.length === 3) {
        if (parts[0]?.length === 4) {
            birthDate = new Date(parts[0], parts[1] - 1, parts[2]);
        } else {
            birthDate = new Date(parts[2], parts[1] - 1, parts[0]);
        }
    } else {
        return "Invalid date format";
    }

    const today = new Date();
    let age = today?.getFullYear() - birthDate?.getFullYear();
    let months = today?.getMonth() - birthDate?.getMonth();

    if (months < 0 || (months === 0 && today?.getDate() < birthDate?.getDate())) {
        age--;
        months += 12;
    }

    return months > 0 ? `${age} Year /${months} Month` : `${age} Year`;
};

/**
 * Resolve nominee age for display — supports stored age, nomineeAge, or DOB-derived age.
 */
export const formatNomineeAge = (nominee) => {
    if (!nominee || typeof nominee !== "object") return "N/A";

    const storedAge = nominee.nomineeAge ?? nominee.age;
    if (storedAge !== null && storedAge !== undefined && String(storedAge).trim() !== "") {
        const raw = String(storedAge).trim();
        if (/year/i.test(raw)) return raw;
        const num = Number(raw);
        if (!Number.isNaN(num) && num >= 0) {
            return num === 1 ? "1 Year" : `${num} Years`;
        }
        return raw;
    }

    const dob = nominee.dateOfBirth ?? nominee.dob ?? nominee.nomineeDob;
    if (dob) {
        const parsed = dayjs(dob);
        if (parsed.isValid()) {
            const calculated = calculateAgeAndMonth(parsed.format("YYYY-MM-DD"));
            if (calculated && calculated !== "Invalid date format") {
                return calculated;
            }
        }
    }

    return "N/A";
};

/** Normalize address proof file paths from member record (field names vary by source). */
export const getAddressProofFiles = (document, uploadDocuments) => {
    const fromUpload = uploadDocuments?.addressProof?.addressProofFile;
    if (fromUpload) {
        const uploadList = Array.isArray(fromUpload) ? fromUpload : [fromUpload];
        const filteredUpload = uploadList.filter(Boolean);
        if (filteredUpload.length > 0) return filteredUpload;
    }

    const apd = document?.addressProofDetails || {};
    const raw =
        apd.uploadAddressProof ??
        apd.addressProofFile ??
        apd.addressProofImage ??
        [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (raw) return [raw];
    return [];
};

/** Whether the separate address proof review/upload block should appear. */
export const shouldShowAddressProofSection = (document) => {
    if (!document) return false;

    const hasFiles = getAddressProofFiles(document).length > 0;
    const hasPresentAddress =
        Boolean(document.addressProofDetails?.presentAddressProofType) ||
        Boolean(document.addressProofDetails?.presentAddress);

    if (document.isAddressSameAsAadhaar === true) {
        return hasFiles || hasPresentAddress;
    }

    return document.isAddressSameAsAadhaar === false || hasFiles || hasPresentAddress;
};

export const getValuebyId = (value, data) => {
    return data?.find((val) => val?.label === value)?.value
}

export const getLabelbyId = (value, data) => {
    // return data?.find((val) => val?.value === value)?.label
    const temp = data?.find((ele) => {
        return ele?.value === value || ele?.label === value;
    });

    return temp ? temp?.label : undefined;
}

export const capitalizeFirstLetterOfWords = (input) => {
    return input
        ?.split(' ') // Split into words
        ?.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize first letter & make rest lowercase
        ?.join(' '); // Join back into a string
};

export const PaymentShowBox = ({ value }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                padding: "3px 5px",
                fontSize: "11px",
                color: "#fff",
                borderRadius: "15px",
                backgroundColor: value === "SUCCESS" ? theme.palette.success.main : (value === "FAILURE" || value?.toLowerCase() === "cancel") ? theme.palette.error.main : value === "CANCEL" ? theme.palette.error.main : value === "PENDING" ? theme.palette.bgLightBlack.main : ""
            }}
        >
            {value}
        </Box >
    );
};

export function CommonStatusBox({ label, height, fontSize }) {
    const safeLabel = label || "-";
    const bgColor = safeLabel === "Re-Uploaded" ? "#F8D270"
        : safeLabel === "Partial-Rejected" ? "#eb5454"
        : safeLabel === "Disabled" ? "#eb5454"
        : safeLabel === "Opted Out" ? "#b45309"
        : "#FFFFFF";
    return (
        <>
            <Chip label={safeLabel}
                sx={{
                    backgroundColor: bgColor,
                    color: "#FFFFFF",
                    fontSize: fontSize || "12px", height: height || "20px",
                    borderRadius:'6px'
                }}
            />
        </>
    )
}

export function CommonStatusChip({ label }) {
    const safeLabel = label || "-";
    return (
        <>
            <Chip label={safeLabel}
                sx={{
                    backgroundColor: safeLabel === "Pending" ?
                        "rgba(255, 243, 219, 1)" : (safeLabel === "Rejected" || safeLabel === "Inactive") ?
                            "rgba(255, 240, 241, 1)" : (safeLabel === "Approved" || safeLabel === "Active") ? "rgba(231, 248, 241, 1)" : "#FFFFFF",
                    color: safeLabel === "Pending" ?
                        "rgba(252, 171, 17, 1)" : (safeLabel === "Rejected" || safeLabel === "Inactive") ?
                            "rgba(227, 54, 41, 1)" : (safeLabel === "Approved" || safeLabel === "Active") ? "rgba(11, 176, 108, 1)" : "#FFFFFF",
                    fontSize: "12px",
                    height: "26px",
                    borderRadius:'6px',
                    border:`2px solid ${safeLabel === "Pending" ?
                        "rgba(252, 171, 17, 1)" : (safeLabel === "Rejected" || safeLabel === "Inactive") ?
                            "rgba(227, 54, 41, 1)" : (safeLabel === "Approved" || safeLabel === "Active") ? "rgba(11, 176, 108, 1)" : "#FFFFFF"}`
                }}
            />
        </>
    )
}